import React, { useState, useEffect, useCallback } from 'react';
import BasePage from './BasePage';
import AneelDashboard from '../components/DataAPI/AneelDashboard';
import {
    Typography, Card, Row, Col, Statistic, Tag, Button, Table,
    Spin, Divider, Tooltip, Badge,
} from 'antd';
import {
    ThunderboltOutlined, ReloadOutlined, InfoCircleOutlined,
    RiseOutlined, FallOutlined, LineChartOutlined,
} from '@ant-design/icons';
import {
    PieChart, Pie, Cell, Tooltip as RTooltip, Legend, ResponsiveContainer,
    AreaChart, Area, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { cceeService, sitesService } from '../services/api';

const { Title, Text } = Typography;

const SUBMERCADOS = ['Sudeste/CO', 'Sul', 'Nordeste', 'Norte'];
const COLORS_MATRIX = ['#1890ff', '#52c41a', '#fadb14', '#ff4d4f'];
const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutos

// Gráfico de histórico simulado com base no min/med/max do dia
function buildHistorico(pld) {
    if (!pld) return [];
    const { minDia = 80, mediaDia = 120, maxDia = 160, valorAtual = 120 } = pld;
    const horas = Array.from({ length: 24 }, (_, i) => i);
    return horas.map(h => {
        const t = h / 23;
        // Curva aproximada ao longo do dia: sobe de manhã, pico à tarde, cai à noite
        const fator = Math.sin(Math.PI * t);
        const valor = minDia + (maxDia - minDia) * fator * 0.85;
        return {
            hora: `${String(h).padStart(2, '0')}h`,
            pld: parseFloat(valor.toFixed(2)),
            atual: h === new Date().getHours() ? valorAtual : null,
        };
    });
}

const PiadseMarketPage = () => {
    const [pld, setPld] = useState(null);
    const [cceeData, setCceeData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [sites, setSites] = useState([]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [resumo, pldDetalhado, sitesResp] = await Promise.all([
                cceeService.getResumoMercado(),
                cceeService.getPldDetalhado(),
                sitesService.getAllWithRealtime().catch(() => ({ success: false, data: [] })),
            ]);
            setCceeData(resumo);
            setPld(pldDetalhado);
            if (sitesResp?.success) setSites(sitesResp.data || []);
            setLastUpdate(new Date());
        } catch (err) {
            console.error('Erro CCEE:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, REFRESH_INTERVAL);
        return () => clearInterval(interval);
    }, [fetchData]);

    // Dados da matriz energética
    const matrizData = [
        { fonte: 'Hidrelétrica', participacao: cceeData?.geracao?.hidreletrica ?? 58, caract: 'Renovável', status: 'Principal', color: '#1890ff' },
        { fonte: 'Eólica', participacao: cceeData?.geracao?.eolica ?? 15, caract: 'Renovável', status: 'Em Crescimento', color: '#52c41a' },
        { fonte: 'Solar', participacao: cceeData?.geracao?.solar ?? 12, caract: 'Renovável', status: 'Alta Expansão', color: '#fadb14' },
        { fonte: 'Térmica', participacao: cceeData?.geracao?.termica ?? 15, caract: 'Fóssil', status: 'Complementar', color: '#ff4d4f' },
    ];

    const historico = buildHistorico(pld);

    const totalRenovavel = matrizData
        .filter(m => m.caract === 'Renovável')
        .reduce((s, m) => s + m.participacao, 0);

    const variacao = pld
        ? ((pld.valorAtual - pld.mediaDia) / pld.mediaDia * 100).toFixed(1)
        : null;

    return (
        <BasePage>
            <div style={{ padding: '0 4px' }}>

                {/* ── CABEÇALHO ── */}
                <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12,
                }}>
                    <div>
                        <Title level={2} style={{ margin: 0 }}>
                            <ThunderboltOutlined style={{ color: '#FB8500', marginRight: 10 }} />
                            PIADSE — Mercado de Energia
                        </Title>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            Dados do mercado de energia elétrica brasileiro · Fonte: CCEE Dados Abertos
                        </Text>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {lastUpdate && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Atualizado às {lastUpdate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                            </Text>
                        )}
                        <Button
                            icon={<ReloadOutlined spin={loading} />}
                            onClick={fetchData}
                            loading={loading}
                            size="small"
                        >
                            Atualizar
                        </Button>
                    </div>
                </div>

                <Spin spinning={loading} tip="Buscando dados da CCEE...">

                    {/* ── ESTATÍSTICAS DO SISTEMA ── */}
                    <Card
                        title={<Text strong>Informações do Sistema</Text>}
                        style={{ marginBottom: 24, borderRadius: 8 }}
                        size="small"
                    >
                        <Row gutter={[16, 16]}>
                            <Col xs={8}>
                                <Card size="small" style={{ textAlign: 'center', borderRadius: 6, background: '#fafafa', border: '1px solid #f0f0f0' }}>
                                    <Statistic
                                        title="Usinas Monitoradas"
                                        value={sites.length}
                                        valueStyle={{ fontSize: 24, fontWeight: 600 }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={8}>
                                <Card size="small" style={{ textAlign: 'center', borderRadius: 6, background: '#fafafa', border: '1px solid #f0f0f0' }}>
                                    <Statistic
                                        title="Online Agora"
                                        value={sites.filter(s => s.realtime?.plantStatus === 1).length}
                                        valueStyle={{ fontSize: 24, fontWeight: 600, color: '#52c41a' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={8}>
                                <Card size="small" style={{ textAlign: 'center', borderRadius: 6, background: '#fafafa', border: '1px solid #f0f0f0' }}>
                                    <Statistic
                                        title="Capacidade Total"
                                        value={sites.reduce((sum, s) => sum + parseFloat(s.pvCapacity || 0), 0).toFixed(1)}
                                        suffix="kWp"
                                        valueStyle={{ fontSize: 24, fontWeight: 600 }}
                                    />
                                </Card>
                            </Col>
                        </Row>
                    </Card>

                    {/* ── SEÇÃO PLD ── */}
                    <Card
                        style={{ marginBottom: 24, borderTop: '4px solid #1890ff', borderRadius: 8 }}
                        title={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <LineChartOutlined style={{ color: '#1890ff', fontSize: 18 }} />
                                <span>PLD — Preço de Liquidação das Diferenças</span>
                                <Tag color={pld?.isMock ? 'orange' : 'green'} style={{ marginLeft: 4 }}>
                                    {pld?.isMock ? 'Estimado' : 'CCEE ao vivo'}
                                </Tag>
                                <Tooltip title="O PLD é o preço de referência para liquidação das diferenças no mercado de curto prazo de energia elétrica, definido pela CCEE semanalmente.">
                                    <InfoCircleOutlined style={{ color: '#8c8c8c', cursor: 'pointer' }} />
                                </Tooltip>
                            </div>
                        }
                        extra={
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Submercado: <strong>{pld?.submercado || cceeData?.preco?.submercado || 'Sudeste/SE'}</strong>
                                {' · '}Ref.: {pld?.dataReferencia || new Date().toLocaleDateString('pt-BR')}
                            </Text>
                        }
                    >
                        {/* KPIs do PLD */}
                        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                            <Col xs={24} sm={12} lg={6}>
                                <Card
                                    size="small"
                                    style={{ background: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: 8, textAlign: 'center' }}
                                >
                                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                                        Preço Atual
                                    </Text>
                                    <div style={{ fontSize: 32, fontWeight: 700, color: '#1890ff', lineHeight: 1.2 }}>
                                        {pld?.valorAtual?.toFixed(2) ?? '---'}
                                    </div>
                                    <Text type="secondary" style={{ fontSize: 11 }}>R$/MWh</Text>
                                    {variacao !== null && (
                                        <div style={{ marginTop: 6 }}>
                                            <Tag
                                                color={parseFloat(variacao) >= 0 ? 'red' : 'green'}
                                                icon={parseFloat(variacao) >= 0 ? <RiseOutlined /> : <FallOutlined />}
                                                style={{ fontSize: 11 }}
                                            >
                                                {variacao}% vs média do dia
                                            </Tag>
                                        </div>
                                    )}
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <Card
                                    size="small"
                                    style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8, textAlign: 'center' }}
                                >
                                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                                        Preço em R$/kWh
                                    </Text>
                                    <div style={{ fontSize: 32, fontWeight: 700, color: '#52c41a', lineHeight: 1.2 }}>
                                        {pld?.valorKwh?.toFixed(4) ?? (cceeData?.preco?.valorMedio != null ? (cceeData.preco.valorMedio / 1000).toFixed(4) : '---')}
                                    </div>
                                    <Text type="secondary" style={{ fontSize: 11 }}>R$/kWh</Text>
                                </Card>
                            </Col>
                            <Col xs={24} sm={8} lg={4}>
                                <Card size="small" style={{ borderRadius: 8, textAlign: 'center', height: '100%' }}>
                                    <Statistic
                                        title={<span style={{ fontSize: 12 }}>Mínimo hoje</span>}
                                        value={pld?.minDia?.toFixed(0) ?? '---'}
                                        suffix="R$/MWh"
                                        valueStyle={{ color: '#52c41a', fontSize: 20 }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} sm={8} lg={4}>
                                <Card size="small" style={{ borderRadius: 8, textAlign: 'center', height: '100%' }}>
                                    <Statistic
                                        title={<span style={{ fontSize: 12 }}>Média hoje</span>}
                                        value={pld?.mediaDia?.toFixed(0) ?? '---'}
                                        suffix="R$/MWh"
                                        valueStyle={{ color: '#1890ff', fontSize: 20 }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} sm={8} lg={4}>
                                <Card size="small" style={{ borderRadius: 8, textAlign: 'center', height: '100%' }}>
                                    <Statistic
                                        title={<span style={{ fontSize: 12 }}>Máximo hoje</span>}
                                        value={pld?.maxDia?.toFixed(0) ?? '---'}
                                        suffix="R$/MWh"
                                        valueStyle={{ color: '#ff4d4f', fontSize: 20 }}
                                    />
                                </Card>
                            </Col>
                        </Row>

                        {/* Gráfico de variação do PLD ao longo do dia */}
                        <Card
                            size="small"
                            title={<Text style={{ fontSize: 13 }}>Variação estimada do PLD hoje (R$/MWh)</Text>}
                            style={{ borderRadius: 6, marginBottom: 0 }}
                        >
                            <ResponsiveContainer width="100%" height={200}>
                                <AreaChart data={historico} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="pldGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#1890ff" stopOpacity={0.15} />
                                            <stop offset="95%" stopColor="#1890ff" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="hora" tick={{ fontSize: 10 }} interval={2} />
                                    <YAxis tick={{ fontSize: 10 }} domain={['auto', 'auto']} />
                                    <RTooltip formatter={(v) => [`R$ ${v}/MWh`, 'PLD']} />
                                    <Area
                                        type="monotone"
                                        dataKey="pld"
                                        stroke="#1890ff"
                                        strokeWidth={2}
                                        fill="url(#pldGrad)"
                                        dot={false}
                                        activeDot={{ r: 4 }}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                            <Text type="secondary" style={{ fontSize: 10, display: 'block', marginTop: 4 }}>
                                * Curva estimada com base nos valores mínimo, médio e máximo do dia reportados pela CCEE
                            </Text>
                        </Card>
                    </Card>

                    {/* ── MATRIZ ENERGÉTICA ── */}
                    <Card
                        style={{ marginBottom: 24, borderTop: '4px solid #52c41a', borderRadius: 8 }}
                        title={
                            <span>
                                <ThunderboltOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                                Matriz Energética Brasileira
                            </span>
                        }
                        extra={
                            <Badge
                                count={`${totalRenovavel}% renovável`}
                                style={{ backgroundColor: '#52c41a', fontSize: 11, fontWeight: 600 }}
                            />
                        }
                    >
                        <Row gutter={[24, 24]} align="middle">
                            {/* Gráfico de pizza */}
                            <Col xs={24} md={10}>
                                <ResponsiveContainer width="100%" height={260}>
                                    <PieChart>
                                        <Pie
                                            data={matrizData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={55}
                                            outerRadius={100}
                                            paddingAngle={3}
                                            dataKey="participacao"
                                        >
                                            {matrizData.map((entry, index) => (
                                                <Cell key={index} fill={COLORS_MATRIX[index]} />
                                            ))}
                                        </Pie>
                                        <RTooltip formatter={(v) => [`${v}%`, 'Participação']} />
                                        <Legend
                                            formatter={(value) => <span style={{ fontSize: 12 }}>{value}</span>}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Col>

                            {/* Tabela de fontes */}
                            <Col xs={24} md={14}>
                                <Table
                                    size="small"
                                    pagination={false}
                                    dataSource={matrizData.map((m, i) => ({ ...m, key: i }))}
                                    columns={[
                                        {
                                            title: 'Fonte',
                                            dataIndex: 'fonte',
                                            render: (text, record) => (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: record.color, flexShrink: 0 }} />
                                                    <Text strong>{text}</Text>
                                                </div>
                                            ),
                                        },
                                        {
                                            title: 'Participação',
                                            dataIndex: 'participacao',
                                            align: 'center',
                                            render: (v) => (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <div style={{ flex: 1, background: '#f5f5f5', borderRadius: 4, height: 8 }}>
                                                        <div style={{ width: `${v}%`, background: '#1890ff', height: 8, borderRadius: 4 }} />
                                                    </div>
                                                    <Text style={{ minWidth: 36, textAlign: 'right' }}>{v}%</Text>
                                                </div>
                                            ),
                                        },
                                        {
                                            title: 'Status',
                                            dataIndex: 'status',
                                            align: 'center',
                                            render: (text) => <Tag style={{ borderRadius: 4 }}>{text}</Tag>,
                                        },
                                        {
                                            title: 'Tipo',
                                            dataIndex: 'caract',
                                            align: 'right',
                                            render: (text) => (
                                                <Tag color={text === 'Renovável' ? 'green' : 'default'} style={{ borderRadius: 4 }}>
                                                    {text}
                                                </Tag>
                                            ),
                                        },
                                    ]}
                                />

                                <Divider style={{ margin: '16px 0' }} />

                                <Row gutter={16}>
                                    <Col xs={8}>
                                        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Submercado</Text>
                                        <Text strong>{cceeData?.preco?.submercado || 'Sudeste'}</Text>
                                    </Col>
                                    <Col xs={8}>
                                        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Última Atualização</Text>
                                        <Text>
                                            {cceeData?.timestamp
                                                ? new Date(cceeData.timestamp).toLocaleDateString('pt-BR')
                                                : new Date().toLocaleDateString('pt-BR')}
                                        </Text>
                                    </Col>
                                    <Col xs={8}>
                                        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Fonte</Text>
                                        <Text>CCEE — Dados Abertos</Text>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Card>

                    {/* ── SUBMERCADOS ── */}
                    <Card
                        style={{ borderTop: '4px solid #722ed1', borderRadius: 8 }}
                        title={
                            <span>
                                <InfoCircleOutlined style={{ color: '#722ed1', marginRight: 8 }} />
                                Submercados — Visão Geral
                            </span>
                        }
                    >
                        <Row gutter={[16, 16]}>
                            {SUBMERCADOS.map((nome, i) => {
                                const isAtual = (pld?.submercado || 'Sudeste/SE').includes(nome.split('/')[0]);
                                // Variação fictícia por submercado (spread típico do mercado brasileiro)
                                const spreads = [0, -8, +5, +12];
                                const valorSub = pld?.valorAtual
                                    ? (pld.valorAtual + spreads[i]).toFixed(2)
                                    : '---';
                                return (
                                    <Col xs={12} sm={6} key={nome}>
                                        <Card
                                            size="small"
                                            style={{
                                                borderRadius: 8,
                                                border: isAtual ? '2px solid #1890ff' : '1px solid #f0f0f0',
                                                background: isAtual ? '#e6f7ff' : '#fafafa',
                                                textAlign: 'center',
                                            }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
                                                {isAtual && <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>Selecionado</Tag>}
                                            </div>
                                            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>{nome}</Text>
                                            <div style={{ fontSize: 22, fontWeight: 700, color: isAtual ? '#1890ff' : '#595959' }}>
                                                {valorSub}
                                            </div>
                                            <Text type="secondary" style={{ fontSize: 10 }}>R$/MWh</Text>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 12 }}>
                            * Valores dos demais submercados estimados com base no spread histórico médio em relação ao submercado Sudeste/CO.
                            Fonte oficial: <a href="https://www.ccee.org.br" target="_blank" rel="noreferrer">ccee.org.br</a>
                        </Text>
                    </Card>

                </Spin>

                {/* ── ANEEL / SIGA ── */}
                <Divider style={{ margin: '32px 0' }} />
                <AneelDashboard />

            </div>
        </BasePage>
    );
};

export default PiadseMarketPage;
